import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { getSurveyList } from "../api/survey.api";
import type {
  SurveyListFilters as SurveyListFiltersType,
  SurveyListItemForAdminResponse,
  SurveyListSorting,
} from "../types/survey.types";
import {
  buildSurveyListApiFilters,
  buildSurveyListSortKey,
  DEFAULT_SURVEY_LIST_SORTING,
  SURVEY_LIST_PAGE_SIZE,
} from "../utils/survey.utils";

export type { SurveyListFilters } from "../types/survey.types";

export interface UseGetSurveyListRequest {
  search: string;
  statusFilter: string;
  filters: SurveyListFiltersType;
  sorting: SurveyListSorting;
  pagination: { page: number; totalCount: number };
}

interface UseGetSurveyListReturn {
  surveys: SurveyListItemForAdminResponse[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  filters: SurveyListFiltersType;
  setFilters: Dispatch<SetStateAction<SurveyListFiltersType>>;
  sorting: SurveyListSorting;
  setSorting: (sorting: SurveyListSorting) => void;
  page: number;
  setPage: (page: number) => void;
  totalCount: number;
  refreshSurveys: () => Promise<void>;
  hasInitialData: boolean;
}

export const useGetSurveyList = (): UseGetSurveyListReturn => {
  const [surveys, setSurveys] = useState<SurveyListItemForAdminResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filters, setFilters] = useState<SurveyListFiltersType>({});
  const [sorting, setSorting] = useState<SurveyListSorting>(DEFAULT_SURVEY_LIST_SORTING);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [hasInitialData, setHasInitialData] = useState(false);
  const requestSequenceRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter, filters]);

  const fetchSurveys = useCallback(async () => {
    const requestSequence = ++requestSequenceRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await getSurveyList({
        ...buildSurveyListApiFilters(debouncedSearch, statusFilter, filters),
        index: page - 1,
        size: SURVEY_LIST_PAGE_SIZE,
        sort_key: buildSurveyListSortKey(sorting),
        sort_direction: sorting.sortKey ? undefined : sorting.direction,
      });
      if (requestSequence !== requestSequenceRef.current) return;
      setSurveys([...response.list]);
      setTotalCount(response.paging?.total ?? 0);
      setHasInitialData(true);
    } catch (fetchError: unknown) {
      if (requestSequence !== requestSequenceRef.current) return;
      console.error("Failed to fetch surveys:", fetchError);
      setError("Failed to load surveys");
      setSurveys([]);
      setTotalCount(0);
    } finally {
      if (requestSequence === requestSequenceRef.current) setLoading(false);
    }
  }, [debouncedSearch, statusFilter, filters, sorting, page]);

  const refreshSurveys = useCallback(async () => {
    await fetchSurveys();
  }, [fetchSurveys]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  return {
    surveys,
    loading,
    error,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filters,
    setFilters,
    sorting,
    setSorting,
    page,
    setPage,
    totalCount,
    refreshSurveys,
    hasInitialData,
  };
};
